import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useNavigate } from "react-router-dom";

const categories = [
  { key: "shoes", url: "https://kaspreadsheet.com/category-911-Shoes.html" },
  { key: "hoodies", url: "https://kaspreadsheet.com/search-912/name/Shoes.html" },
  { key: "tshirts", url: "https://kaspreadsheet.com/search-913/name/Shoes.html" },
  { key: "jackets", url: "https://kaspreadsheet.com/search-914/name/Shoes.html" },
  { key: "pants", url: "https://kaspreadsheet.com/search-915/name/Shoes.html" },
  { key: "headwear", url: "https://kaspreadsheet.com/search-916/name/Shoes.html" },
  { key: "sets", url: "https://kaspreadsheet.com/search-917/name/Shoes.html" },
  { key: "underwear", url: "https://kaspreadsheet.com/search-918/name/Shoes.html" },
  { key: "jersey", url: "https://kaspreadsheet.com/search-919/name/Shoes.html" },
  { key: "accessories", url: "https://kaspreadsheet.com/search-920/name/Shoes.html" },
  { key: "bags", url: "https://kaspreadsheet.com/search-921/name/Shoes.html" },
  { key: "belts", url: "https://kaspreadsheet.com/search-922/name/Shoes.html" },
  { key: "perfumes", url: "https://kaspreadsheet.com/search-923/name/Shoes.html" },
  { key: "other", url: "https://kaspreadsheet.com/search-924/name/Shoes.html" },
];

const languages = [
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
  { code: "da", label: "Dansk" },
  { code: "de", label: "Deutsch" },
];

export default function Navbar() {
  const { t } = useTranslation();
  const { lang = "en" } = useParams<{ lang?: string }>();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catDropdownOpen, setCatDropdownOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCatDropdownOpen(false);
    setLangDropdownOpen(false);
  }, [lang]);

  const handleLanguageChange = (code: string) => {
    const currentPath = window.location.pathname;
    const pathParts = currentPath.split("/").filter(Boolean);

    if (pathParts.length === 0) {
      navigate(`/${code}/`);
    } else if (pathParts.length > 0) {
      if (["en", "fr", "da", "de"].includes(pathParts[0])) {
        pathParts[0] = code;
      } else {
        pathParts.unshift(code);
      }
      navigate(`/${pathParts.join("/")}`);
    }
    setLangDropdownOpen(false);
  };

  const getPagePath = (page: string) => {
    return `/${lang}/${page}`;
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-sm border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="container-limit mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link to={`/${lang}/`} className="flex items-center gap-2 shrink-0">
            <span className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              AllChinaBuy
            </span>
            <span className="hidden sm:inline-block text-xs font-label font-medium px-2 py-0.5 rounded-full bg-primary-100 text-primary-700">
              Spreadsheet
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-6">
            <Link to={getPagePath("products")} className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors">
              Products
            </Link>
            {/* Categories Dropdown */}
            <div className="relative">
              <button
                onClick={() => setCatDropdownOpen(!catDropdownOpen)}
                className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors"
              >
                {t("nav.categories")}
                <i className={`ri-arrow-down-s-line transition-transform ${catDropdownOpen ? "rotate-180" : ""}`} />
              </button>
              {catDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 rounded-lg shadow-lg border border-background-200/70 py-2 z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.key}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors"
                    >
                      {t(`categories.${cat.key}`)}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link to={getPagePath("allchinabuy-spreadsheet")} className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors">
              {t("nav.guides")}
            </Link>
            <Link to={getPagePath("allchinabuy-shipping")} className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors">
              {t("nav.shipping")}
            </Link>
            <Link to={getPagePath("allchinabuy-qc")} className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors">
              {t("nav.qc")}
            </Link>
            <Link to={getPagePath("allchinabuy-review")} className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors">
              {t("nav.reviews")}
            </Link>
            <Link to={getPagePath("allchinabuy-safety")} className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors">
              {t("nav.safety")}
            </Link>
          </nav>

          {/* Right side: Language + CTA */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors"
              >
                <i className="ri-global-line" />
                {languages.find((l) => l.code === lang)?.label || "English"}
                <i className={`ri-arrow-down-s-line transition-transform ${langDropdownOpen ? "rotate-180" : ""}`} />
              </button>
              {langDropdownOpen && (
                <div className="absolute top-full right-0 mt-2 w-40 bg-background-50 rounded-lg shadow-lg border border-background-200/70 py-2 z-50">
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => handleLanguageChange(l.code)}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                        lang === l.code
                          ? "text-primary-600 bg-primary-50"
                          : "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
                      }`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="btn-primary text-sm px-4 py-2"
            >
              {t("hero.mainCta")}
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden p-2 text-foreground-700"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-2xl`} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 space-y-3">
          <p className="text-xs font-medium text-foreground-500 uppercase tracking-wide">{t("categories.title")}</p>
          <div className="grid grid-cols-2 gap-2">
            {categories.map((cat) => (
              <a
                key={cat.key}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="text-sm text-foreground-700 hover:text-foreground-950 py-1"
              >
                {t(`categories.${cat.key}`)}
              </a>
            ))}
          </div>
          <div className="border-t border-background-200/70 pt-3 space-y-2">
            <Link to={getPagePath("products")} className="block text-sm font-medium text-foreground-700">
              Products
            </Link>
            <Link to={getPagePath("allchinabuy-spreadsheet")} className="block text-sm font-medium text-foreground-700">
              {t("nav.guides")}
            </Link>
            <Link to={getPagePath("allchinabuy-shipping")} className="block text-sm font-medium text-foreground-700">
              {t("nav.shipping")}
            </Link>
            <Link to={getPagePath("allchinabuy-qc")} className="block text-sm font-medium text-foreground-700">
              {t("nav.qc")}
            </Link>
            <Link to={getPagePath("allchinabuy-review")} className="block text-sm font-medium text-foreground-700">
              {t("nav.reviews")}
            </Link>
            <Link to={getPagePath("allchinabuy-safety")} className="block text-sm font-medium text-foreground-700">
              {t("nav.safety")}
            </Link>
          </div>
          <div className="border-t border-background-200/70 pt-3 flex gap-2">
            {languages.map((l) => (
              <button
                key={l.code}
                onClick={() => {
                  handleLanguageChange(l.code);
                  setMobileOpen(false);
                }}
                className={`text-sm px-3 py-1 rounded-md transition-colors ${
                  lang === l.code
                    ? "bg-primary-100 text-primary-700"
                    : "bg-background-100 text-foreground-600"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="btn-primary w-full text-sm justify-center"
          >
            {t("hero.mainCta")}
          </a>
        </div>
      )}
    </header>
  );
}